import { useEffect, useMemo, useRef, useState } from 'react';
import { message, Modal } from 'antd';
import {
  CoverOptionsResponse,
  HardwareLinePreviewChange,
  HardwareReplacementCandidateDto,
  OrderRecalcPreviewRequest,
  OrderRecalcPreviewResult,
  OrderVersionDto,
  OrderVersionLineItemDto,
} from '../../../../interfaces/OrderDetail';
import { getStateList } from '../../../../store/actions/adminActionCreators';
import {
  updateOrderDetails,
  updateCover,
  getCoverOptions,
  appendOrderNote,
  stampOrderChangelog,
  getHardwareReplacementCandidates,
  swapHardwareLine,
  previewOrderRecalc,
} from '../../../../axios/orderDetail';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';

const PREVIEW_DEBOUNCE_MS = 400;

interface PreviewSnapshot {
  hwPendingChanges: HardwarePendingChange[];
  selectedColorId: number | null;
  selectedMaterialId: number | null;
}

export type StateCode = { id: string; stateName: string };

export interface HardwarePendingChange {
  lineIdcol: number;
  item: string;
  originalItem: string | null;
  originalDescription: string;
  description: string;
  price: number | null;
  quantity: number;
  originalQuantity: number | null;
}

export interface OrderDetailsDraft {
  poNumber: string;
  shipVia: string;
  fob: string;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  poolOwnerFirstName: string;
  poolOwnerLastName: string;
  poolOwnerAddress: string;
  poolOwnerCity: string;
  poolOwnerState: string;
  poolOwnerZip: string;
}

function draftFromVersion(version: OrderVersionDto): OrderDetailsDraft {
  return {
    poNumber: version.poNumber ?? '',
    shipVia: version.shipVia ?? '',
    fob: version.fob ?? '',
    shippingName: version.shippingName ?? '',
    shippingAddress: version.shippingAddress ?? '',
    shippingCity: version.shippingCity ?? '',
    shippingState: version.shippingState ?? '',
    shippingZip: version.shippingZip ?? '',
    poolOwnerFirstName: version.poolOwnerFirstName ?? '',
    poolOwnerLastName: version.poolOwnerLastName ?? '',
    poolOwnerAddress: version.poolOwnerAddress ?? '',
    poolOwnerCity: version.poolOwnerCity ?? '',
    poolOwnerState: version.poolOwnerState ?? '',
    poolOwnerZip: version.poolOwnerZip ?? '',
  };
}

interface UseOrderDetailsEditArgs {
  version: OrderVersionDto;
  soNo: string;
  onSaved?: () => void;
}

export interface UseOrderDetailsEditResult {
  editing: boolean;
  saving: boolean;
  loadingOptions: boolean;
  isValid: boolean;
  hasChanges: boolean;
  draft: OrderDetailsDraft;
  setDraft: React.Dispatch<React.SetStateAction<OrderDetailsDraft>>;
  usStateOptions: StateCode[];
  coverOptions: CoverOptionsResponse | null;
  selectedColorId: number | null;
  selectedMaterialId: number | null;
  setSelectedColorId: (id: number) => void;
  setSelectedMaterialId: (id: number) => void;
  hwTargetLine: OrderVersionLineItemDto | null;
  hwCurrentItem: string | null;
  hwCurrentQuantity: number | null;
  hwCandidates: HardwareReplacementCandidateDto[];
  hwLoadingCandidates: boolean;
  hwPendingChanges: HardwarePendingChange[];
  openHardwareReplacement: (line: OrderVersionLineItemDto) => Promise<void>;
  closeHardwareReplacement: () => void;
  confirmHardwareReplacement: (newItem: string, quantity: number) => void;
  handleEdit: () => Promise<void>;
  handleCancel: () => void;
  handleSave: () => Promise<void>;
  previewTotals: OrderRecalcPreviewResult | null;
  previewLoading: boolean;
}

export function useOrderDetailsEdit({
  version,
  soNo,
  onSaved,
}: UseOrderDetailsEditArgs): UseOrderDetailsEditResult {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [draft, setDraft] = useState<OrderDetailsDraft>(() => draftFromVersion(version));
  const [usStateOptions, setUsStateOptions] = useState<StateCode[]>([]);
  const [coverOptions, setCoverOptions] = useState<CoverOptionsResponse | null>(null);
  const [selectedColorId, setSelectedColorId] = useState<number | null>(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);
  const [hwTargetLine, setHwTargetLine] = useState<OrderVersionLineItemDto | null>(null);
  const [hwCandidates, setHwCandidates] = useState<HardwareReplacementCandidateDto[]>([]);
  const [hwLoadingCandidates, setHwLoadingCandidates] = useState(false);
  const [hwPendingChanges, setHwPendingChanges] = useState<HardwarePendingChange[]>([]);
  const [previewTotals, setPreviewTotals] = useState<OrderRecalcPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewSeqRef = useRef(0);

  const isValid = draft.poNumber.trim() !== '' && draft.fob.trim() !== '';

  const fieldChanged = (original: string | null, updated: string) =>
    (original ?? '').trim() !== updated.trim();

  const hasDetailChanges =
    fieldChanged(version.poNumber, draft.poNumber) ||
    fieldChanged(version.shipVia, draft.shipVia) ||
    fieldChanged(version.fob, draft.fob) ||
    fieldChanged(version.shippingName, draft.shippingName) ||
    fieldChanged(version.shippingAddress, draft.shippingAddress) ||
    fieldChanged(version.shippingCity, draft.shippingCity) ||
    fieldChanged(version.shippingState, draft.shippingState) ||
    fieldChanged(version.shippingZip, draft.shippingZip) ||
    fieldChanged(version.poolOwnerFirstName, draft.poolOwnerFirstName) ||
    fieldChanged(version.poolOwnerLastName, draft.poolOwnerLastName) ||
    fieldChanged(version.poolOwnerAddress, draft.poolOwnerAddress) ||
    fieldChanged(version.poolOwnerCity, draft.poolOwnerCity) ||
    fieldChanged(version.poolOwnerState, draft.poolOwnerState) ||
    fieldChanged(version.poolOwnerZip, draft.poolOwnerZip);

  const hasCoverSelectionChanges = coverOptions != null && (
    (coverOptions.colors.find((c) => c.id === selectedColorId)?.descrip ?? '').trim().toLowerCase() !==
      (version.color ?? '').trim().toLowerCase() ||
    (coverOptions.materials.find((m) => m.id === selectedMaterialId)?.descrip ?? '').trim().toLowerCase() !==
      (version.material ?? '').trim().toLowerCase()
  );

  const hasChanges = hasDetailChanges || hasCoverSelectionChanges || hwPendingChanges.length > 0;

  // Debounce a snapshot of ONLY the pricing-relevant staged state — ship-to, pool owner, PO#,
  // FOB, and ship-via never affect price (confirmed by grep against RecalcOrderTotalsAsync/
  // RepriceCoverAsync), so the preview call must never fire for those fields.
  const previewSnapshot = useMemo<PreviewSnapshot>(
    () => ({ hwPendingChanges, selectedColorId, selectedMaterialId }),
    [hwPendingChanges, selectedColorId, selectedMaterialId]
  );
  const debouncedPreviewSnapshot = useDebouncedValue(previewSnapshot, PREVIEW_DEBOUNCE_MS);

  useEffect(() => {
    if (!editing) return;

    const { hwPendingChanges: debouncedHw, selectedColorId: debouncedColorId, selectedMaterialId: debouncedMaterialId } =
      debouncedPreviewSnapshot;

    const debouncedCoverChanged =
      coverOptions != null &&
      ((coverOptions.colors.find((c) => c.id === debouncedColorId)?.descrip ?? '').trim().toLowerCase() !==
        (version.color ?? '').trim().toLowerCase() ||
        (coverOptions.materials.find((m) => m.id === debouncedMaterialId)?.descrip ?? '').trim().toLowerCase() !==
          (version.material ?? '').trim().toLowerCase());

    // Nothing pricing-relevant staged yet — don't fire on entering edit mode with an
    // unchanged snapshot, there's no point previewing unchanged state.
    if (debouncedHw.length === 0 && !debouncedCoverChanged) return;

    const seq = ++previewSeqRef.current;
    setPreviewLoading(true);

    const request: OrderRecalcPreviewRequest = {
      versionLabel: version.versionLabel,
      hardwareChanges: debouncedHw.map<HardwareLinePreviewChange>((c) => ({
        lineIdcol: c.lineIdcol,
        item: c.item,
        quantity: c.quantity,
      })),
      colorId: debouncedColorId ?? undefined,
      materialId: debouncedMaterialId ?? undefined,
    };

    previewOrderRecalc(soNo, request)
      .then((result) => {
        if (previewSeqRef.current !== seq) return; // stale/out-of-order response — discard
        if (result.success) setPreviewTotals(result);
        // else: a staged hardware swap failed re-validation server-side — keep the last good
        // previewTotals in place, this is best-effort UI only and must never block Save.
      })
      .catch(() => {
        // Best-effort — leave the last successful previewTotals in place, no blocking error.
      })
      .finally(() => {
        if (previewSeqRef.current === seq) setPreviewLoading(false);
      });
  }, [debouncedPreviewSnapshot, editing, coverOptions, soNo, version.versionLabel, version.color, version.material]);

  const handleEdit = async () => {
    setDraft(draftFromVersion(version));
    setLoadingOptions(true);
    try {
      const [stateResp, opts] = await Promise.all([
        getStateList() as Promise<any[]>,
        getCoverOptions(soNo).catch(() => null),
      ]);
      const [stateResult] = stateResp as any[];
      if (stateResult?.status === 200) setUsStateOptions(stateResult.data.data);
      if (opts) {
        setCoverOptions(opts);
        setSelectedColorId(
          opts.colors.find(
            (c: any) => c.descrip.trim().toLowerCase() === (version.color ?? '').trim().toLowerCase()
          )?.id ?? null
        );
        setSelectedMaterialId(
          opts.materials.find(
            (m: any) => m.descrip.trim().toLowerCase() === (version.material ?? '').trim().toLowerCase()
          )?.id ?? null
        );
      }
    } finally {
      setLoadingOptions(false);
    }
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setCoverOptions(null);
    setSelectedColorId(null);
    setSelectedMaterialId(null);
    setHwPendingChanges([]);
    setPreviewTotals(null);
  };

  const openHardwareReplacement = async (line: OrderVersionLineItemDto) => {
    if (line.lineIdcol == null) return;
    setHwTargetLine(line);
    setHwCandidates([]);
    setHwLoadingCandidates(true);
    try {
      const result = await getHardwareReplacementCandidates(soNo, line.lineIdcol);
      if (!result.isEditable) {
        message.error('This item is not eligible for replacement.');
        setHwTargetLine(null);
        return;
      }
      setHwCandidates(result.candidates);
    } catch (err: any) {
      const isEditable = err?.response?.data?.isEditable;
      message.error(
        isEditable === false
          ? 'This item is not eligible for replacement.'
          : 'Failed to load replacement options. Please try again.'
      );
      setHwTargetLine(null);
    } finally {
      setHwLoadingCandidates(false);
    }
  };

  // If this line already has a staged-but-unsaved swap, the modal should reopen showing
  // that pending selection as "current" rather than reverting to the true DB original.
  const hwPendingForTarget =
    hwTargetLine != null
      ? hwPendingChanges.find((c) => c.lineIdcol === hwTargetLine.lineIdcol) ?? null
      : null;
  const hwCurrentItem = hwPendingForTarget?.item ?? hwTargetLine?.item ?? null;
  const hwCurrentQuantity = hwPendingForTarget?.quantity ?? hwTargetLine?.quantity ?? null;

  const closeHardwareReplacement = () => {
    setHwTargetLine(null);
    setHwCandidates([]);
  };

  const confirmHardwareReplacement = (newItem: string, quantity: number) => {
    if (hwTargetLine?.lineIdcol == null) return;
    // Stage the change locally only — nothing is persisted until the top-level
    // Save button runs handleSave, same as every other field in this hook.
    const candidate = hwCandidates.find((c) => c.item === newItem);
    const targetLineIdcol = hwTargetLine.lineIdcol;
    const newChange: HardwarePendingChange = {
      lineIdcol: targetLineIdcol,
      item: newItem,
      originalItem: hwTargetLine.item,
      originalDescription: hwTargetLine.description,
      description: candidate?.description ?? hwTargetLine.description,
      price: candidate?.price ?? null,
      quantity,
      originalQuantity: hwTargetLine.quantity,
    };
    // Upsert by lineIdcol so staging a change on one line doesn't wipe out changes
    // already staged on other lines.
    setHwPendingChanges((prev) => [
      ...prev.filter((c) => c.lineIdcol !== targetLineIdcol),
      newChange,
    ]);
    closeHardwareReplacement();
  };

  const handleSave = async () => {
    const changes: string[] = [];

    const check = (label: string, original: string | null, updated: string) => {
      const orig = (original ?? '').trim();
      const upd = updated.trim();
      if (orig !== upd) changes.push(`${label} from "${orig || 'none'}" to "${upd || 'none'}"`);
    };

    check('PO#', version.poNumber, draft.poNumber);
    check('ship-to name', version.shippingName, draft.shippingName);
    check('ship-to address', version.shippingAddress, draft.shippingAddress);
    check('ship-to city', version.shippingCity, draft.shippingCity);
    check('ship-to state', version.shippingState, draft.shippingState);
    check('ship-to zip', version.shippingZip, draft.shippingZip);
    check('pool owner first name', version.poolOwnerFirstName, draft.poolOwnerFirstName);
    check('pool owner last name', version.poolOwnerLastName, draft.poolOwnerLastName);
    check('pool owner address', version.poolOwnerAddress, draft.poolOwnerAddress);
    check('pool owner city', version.poolOwnerCity, draft.poolOwnerCity);
    check('pool owner state', version.poolOwnerState, draft.poolOwnerState);
    check('pool owner zip', version.poolOwnerZip, draft.poolOwnerZip);
    check('FOB', version.fob, draft.fob);
    check('Ship Via', version.shipVia, draft.shipVia);

    const changed = (original: string | null, updated: string) =>
      (original ?? '').trim() !== updated.trim() ? updated.trim() : undefined;

    setSaving(true);
    try {
      await updateOrderDetails(soNo, {
        poNumber: changed(version.poNumber, draft.poNumber),
        shipVia: changed(version.shipVia, draft.shipVia),
        fob: changed(version.fob, draft.fob),
        shippingName: changed(version.shippingName, draft.shippingName),
        shippingAddress: changed(version.shippingAddress, draft.shippingAddress),
        shippingCity: changed(version.shippingCity, draft.shippingCity),
        shippingState: changed(version.shippingState, draft.shippingState),
        shippingZip: changed(version.shippingZip, draft.shippingZip),
        poolOwnerFirstName: changed(version.poolOwnerFirstName, draft.poolOwnerFirstName),
        poolOwnerLastName: changed(version.poolOwnerLastName, draft.poolOwnerLastName),
        poolOwnerAddress: changed(version.poolOwnerAddress, draft.poolOwnerAddress),
        poolOwnerCity: changed(version.poolOwnerCity, draft.poolOwnerCity),
        poolOwnerState: changed(version.poolOwnerState, draft.poolOwnerState),
        poolOwnerZip: changed(version.poolOwnerZip, draft.poolOwnerZip),
      });
      if (changes.length > 0) {
        await appendOrderNote(soNo, `Updated: ${changes.join(', ')}.`, version.versionLabel);
      }
    } catch {
      message.error('Failed to save. Please try again.');
      setSaving(false);
      return;
    }

    let coverChangesForPDF: string[] = [];

    if (coverOptions && selectedColorId != null && selectedMaterialId != null) {
      const colorDescrip = coverOptions.colors.find((c) => c.id === selectedColorId)?.descrip ?? '';
      const materialDescrip = coverOptions.materials.find((m) => m.id === selectedMaterialId)?.descrip ?? '';
      const coverChanges: string[] = [];
      if (colorDescrip.trim().toLowerCase() !== (version.color ?? '').trim().toLowerCase())
        coverChanges.push(`color from "${version.color || 'none'}" to "${colorDescrip}"`);
      if (materialDescrip.trim().toLowerCase() !== (version.material ?? '').trim().toLowerCase())
        coverChanges.push(`material from "${version.material || 'none'}" to "${materialDescrip}"`);

      try {
        await updateCover(soNo, selectedColorId, selectedMaterialId, colorDescrip, materialDescrip, version.versionLabel);
        if (coverChanges.length > 0) {
          coverChangesForPDF = coverChanges;
          await appendOrderNote(soNo, `Updated: ${coverChanges.join(', ')}.`, version.versionLabel);
        }
      } catch {
        message.error('Failed to save cover. Please try again.');
        setSaving(false);
        return;
      }
    }

    let hwChangeForPDF: string | null = null;

    if (hwPendingChanges.length > 0) {
      const hwChanges: string[] = [];
      try {
        // Sequential, not parallel — each swap triggers a server-side recalc of the
        // order totals, and firing those concurrently for multiple lines would race.
        for (const pending of hwPendingChanges) {
          await swapHardwareLine(
            soNo,
            pending.lineIdcol,
            pending.item,
            version.versionLabel,
            pending.quantity
          );
          if ((pending.originalItem ?? '') !== pending.item) {
            hwChanges.push(
              `hardware item from "${pending.originalDescription}" to "${pending.description}"`
            );
          }
          if ((pending.originalQuantity ?? 0) !== pending.quantity) {
            hwChanges.push(
              `hardware quantity for "${pending.description}" from ${pending.originalQuantity ?? 0} to ${pending.quantity}`
            );
          }
        }
        if (hwChanges.length > 0) {
          hwChangeForPDF = hwChanges.join(', ');
          await appendOrderNote(soNo, `Updated: ${hwChangeForPDF}.`, version.versionLabel);
        }
      } catch {
        message.error('Failed to save hardware item change. Please try again.');
        setSaving(false);
        return;
      }
    }

    const allPDFChanges = [
      ...(changes.length > 0 ? [`Updated: ${changes.join(', ')}.`] : []),
      ...(coverChangesForPDF.length > 0 ? [`Updated: ${coverChangesForPDF.join(', ')}.`] : []),
      ...(hwChangeForPDF != null ? [`Updated: ${hwChangeForPDF}.`] : []),
    ];

    if (allPDFChanges.length > 0) {
      try {
        await stampOrderChangelog(soNo, allPDFChanges);
      } catch {
        message.error('Failed to record changes. Please try again.');
      }
    }

    // Only flip editing/saving off together once every save step — including the
    // changelog stamp — has settled, so the Save/Cancel pair (and its spinner) never
    // unmounts into a plain "Edit" button while a save is still in flight.
    setEditing(false);
    setCoverOptions(null);
    setSelectedColorId(null);
    setSelectedMaterialId(null);
    setHwPendingChanges([]);
    setPreviewTotals(null);
    setSaving(false);

    Modal.success({
      title: 'Changes Saved',
      content:
        'Your changes have been saved and will be finalized once Meyco completes its review.',
      style: { paddingBottom: 0 }
    });

    onSaved?.();
  };

  return {
    editing,
    saving,
    loadingOptions,
    isValid,
    hasChanges,
    draft,
    setDraft,
    usStateOptions,
    coverOptions,
    selectedColorId,
    selectedMaterialId,
    setSelectedColorId,
    setSelectedMaterialId,
    hwTargetLine,
    hwCurrentItem,
    hwCurrentQuantity,
    hwCandidates,
    hwLoadingCandidates,
    hwPendingChanges,
    openHardwareReplacement,
    closeHardwareReplacement,
    confirmHardwareReplacement,
    handleEdit,
    handleCancel,
    handleSave,
    previewTotals,
    previewLoading,
  };
}
